import { useCallback, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Select, Field, Chip, Btn } from "@/components/kit";
import { exportXlsx, exportCsv } from "@/lib/export";
import { Factory, WarningCircle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const WORKSHOPS = ["AMSKY", "CRON", "KODAK", "PARIBESHAK"];

export default function CurrentStock() {
  const { user } = useAuth();
  const m = useMasters();
  const canManageAllWorkshops = ["super_admin", "admin", "manager"].includes(user?.role);
  const assignedWorkshop = (user?.assigned_workshop || "AMSKY").toUpperCase();
  const [rows, setRows] = useState([]);
  const [plateFilter, setPlateFilter] = useState("");
  const [workshopFilter, setWorkshopFilter] = useState(canManageAllWorkshops ? "" : assignedWorkshop);
  const [hideZeros, setHideZeros] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/inventory/current_stock");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Group by machine, then by plate size (sum across all locations of that machine)
  const perMachine = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const mkey = r.machine_id;
      if (!map[mkey]) map[mkey] = { machine_id: mkey, total: 0, byPlate: {} };
      map[mkey].total += r.stock;
      if (!map[mkey].byPlate[r.plate_size_id]) {
        map[mkey].byPlate[r.plate_size_id] = { plate_size_id: r.plate_size_id, stock: 0 };
      }
      map[mkey].byPlate[r.plate_size_id].stock += r.stock;
    }
    return map;
  }, [rows]);

  const visibleMachines = useMemo(
    () => (m.machines || []).filter(machine =>
      !workshopFilter || machine.name?.toUpperCase() === workshopFilter || machine.code?.toUpperCase() === workshopFilter
    ),
    [m.machines, workshopFilter],
  );
  const visibleMachineIds = useMemo(() => new Set(visibleMachines.map(machine => machine.id)), [visibleMachines]);
  const grandTotal = useMemo(
    () => rows.filter(row => visibleMachineIds.has(row.machine_id)).reduce((sum, row) => sum + row.stock, 0),
    [rows, visibleMachineIds],
  );

  const psFilterMatches = (canonical) => {
    if (!plateFilter) return true;
    return canonical.toLowerCase().includes(plateFilter.toLowerCase());
  };

  const buildMachineRows = (machineId) => {
    const grp = perMachine[machineId];
    if (!grp) return [];
    return Object.values(grp.byPlate)
      .map(x => {
        const ps = m.plate_sizes.find(p => p.id === x.plate_size_id);
        return {
          plate_size_id: x.plate_size_id,
          canonical: ps?.canonical || "-",
          min_stock: ps?.min_stock || 0,
          stock: x.stock,
        };
      })
      .filter(x => psFilterMatches(x.canonical))
      .filter(x => !hideZeros || x.stock !== 0)
      .sort((a, b) => a.canonical.localeCompare(b.canonical));
  };

  const exportAll = () => {
    const flat = [];
    for (const mc of visibleMachines) {
      const machRows = buildMachineRows(mc.id);
      for (const r of machRows) {
        flat.push({ Machine: mc.name, "Plate Size": r.canonical, Stock: r.stock, "Min Stock": r.min_stock });
      }
    }
    exportXlsx("current_stock_by_machine.xlsx", flat, "Current Stock");
  };

  return (
    <div data-testid="stock-page">
      <PageHeader
        title="Current Stock"
        subtitle="Each machine keeps its own stock list. Numbers are derived live from the immutable stock ledger."
        actions={<Btn variant="outline" onClick={exportAll} data-testid="stock-export-xlsx">Export XLSX</Btn>}
      />
      <div className="p-3 sm:p-6 space-y-4 min-w-0">
        {/* Filters */}
        <Card>
          <div className="p-3 flex flex-wrap gap-3 items-center">
            <Field label="Workshop">
              <Select value={workshopFilter} onChange={e=>setWorkshopFilter(e.target.value)} data-testid="stock-workshop-filter">
                <option value="">All</option>
                {WORKSHOPS.map(workshop => <option key={workshop} value={workshop}>{workshop}</option>)}
              </Select>
            </Field>
            <Field label="Filter Plate Size">
              <input
                type="text"
                placeholder="e.g. 550"
                value={plateFilter}
                onChange={e=>setPlateFilter(e.target.value)}
                className="border border-zinc-300 rounded-sm px-2.5 py-1.5 text-sm w-full sm:w-48"
                data-testid="stock-plate-filter"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm mt-5">
              <input type="checkbox" checked={hideZeros} onChange={e=>setHideZeros(e.target.checked)} data-testid="stock-hide-zeros" />
              Hide zero-stock sizes
            </label>
            <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-4">
              {!canManageAllWorkshops && workshopFilter && workshopFilter !== assignedWorkshop && <Chip tone="warn">View only</Chip>}
              <Chip tone="info">Grand Total: <span className="font-mono font-bold ml-1">{grandTotal}</span></Chip>
            </div>
          </div>
        </Card>

        {/* One card per machine */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {visibleMachines.length === 0 && (
            <Card><div className="p-6 text-sm text-zinc-500">No machines set up.</div></Card>
          )}
          {visibleMachines.map(mc => {
            const machineRows = buildMachineRows(mc.id);
            const machineTotal = machineRows.reduce((s, r) => s + r.stock, 0);
            const lowStockCount = machineRows.filter(r => r.min_stock > 0 && r.stock < r.min_stock).length;
            return (
              <Card key={mc.id} testid={`stock-machine-${mc.name}`}>
                <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-950 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Factory size={16} weight="duotone" />
                      <div className="font-heading font-black text-lg tracking-tight uppercase">{mc.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest opacity-70">Total</div>
                      <div className="font-heading font-bold text-2xl leading-none">{machineTotal}</div>
                    </div>
                  </div>
                  {lowStockCount > 0 && (
                    <div className="flex items-center gap-1.5 mt-2 text-[11px] text-amber-300">
                      <WarningCircle size={12} weight="fill" />
                      {lowStockCount} plate size{lowStockCount===1?"":"s"} below minimum
                    </div>
                  )}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      <th className="text-left px-3 py-2">Plate Size</th>
                      <th className="text-right px-3 py-2">Stock</th>
                      <th className="text-right px-3 py-2">Min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {machineRows.length === 0 && (
                      <tr><td colSpan={3} className="text-center py-6 text-zinc-500 text-sm">No stock for this machine.</td></tr>
                    )}
                    {machineRows.map(r => {
                      const low = r.min_stock > 0 && r.stock < r.min_stock;
                      const negative = r.stock < 0;
                      return (
                        <tr key={r.plate_size_id} className={`border-b border-zinc-100 last:border-b-0 ${low?"bg-amber-50":""}`}>
                          <td className="px-3 py-2 font-mono font-semibold">{r.canonical}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${negative ? "text-red-600" : low ? "text-amber-700" : ""}`}>{r.stock}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-zinc-500">{r.min_stock || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
